/* global DetectUserAgent */
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { isNil } from "ramda";

import { useFacebookActions } from "store/FacebookSlice";
import {
  init,
  login,
  getAllAlbums,
  getImagesOfAlbum,
  checkLoggedin,
  getNextPage,
  validatePermissions,
  customLogin,
} from "utils/facebookUtils";
import { jjLogger } from "utils/logUtils";

import useSettings from "./useSettings";

const FACEBOOK_TOKEN_KEY = "fbToken";

const useFacebook = (initialize = false, isOauth = false) => {
  const {
    loadAlbums,
    loadPhotos,
    loadAdditionalPhotos,
    loadAdditionalAlbums,
    changeMode,
    changeModalOpen,
    changeSelectedPhoto,
    changeInitialized,
    changeStatus,
    clearPhotos,
    clearSelectedPhoto,
    resetMode,
    clearAlbums,
    clearStatus,
    changeLoading,
  } = useFacebookActions();
  const [nextPage, setNextPage] = useState(null);
  const {
    settings: { fbAppId },
  } = useSettings();
  const isFB4Android = DetectUserAgent.isFB4Android();

  const { albums, mode, photos, modalOpen, selectedPhoto, initialized, status } = useSelector(
    (state) => state.FacebookSlice,
  );
  const loading = useSelector((state) => (isOauth ? true : state.FacebookSlice.loading));

  useEffect(() => {
    if (initialize && !initialized) {
      init(fbAppId, changeInitialized).catch(() => {
        changeLoading(false);
        jjLogger.logDebug("facebookUtils - init() - error initializing facebook sdk");
      });
    }
  }, []);

  const storeFacebookToken = (token) => {
    sessionStorage.setItem(FACEBOOK_TOKEN_KEY, token);
  };

  const getFacebookToken = () => sessionStorage.getItem(FACEBOOK_TOKEN_KEY);

  const changeModalOpenStorage = (value) => {
    const isFbInAppBrowser = DetectUserAgent.isFBInAppBrowser();
    if (isFbInAppBrowser) {
      sessionStorage.setItem("facebookModal", value);
    }
    changeModalOpen(value);
  };

  const availableAlbums = albums?.filter((album) => !isNil(album?.cover_photo?.images));

  const hasSelectedPhoto = !isNil(selectedPhoto) && !isNil(selectedPhoto?.source);

  const checkLoggedinStatus = () => {
    return checkLoggedin()
      .then((currentStatus) => validatePermissions(currentStatus, getFacebookToken()))
      .then((res) => {
        changeStatus(res);
        changeLoading(false);
      })
      .catch(() => {
        changeLoading(false);
        jjLogger.logDebug("useFacebook - checkLoggedinStatus() - error getting login status to user");
      });
  };

  const loginFacebook = () => {
    changeLoading(true);
    if (isFB4Android) {
      return customLogin(fbAppId);
    }
    return login(fbAppId)
      .then(changeStatus)
      .catch(() => {
        changeLoading(false);
        jjLogger.logDebug("useFacebook - loginFacebook() - error when logging in with facebook");
      });
  };

  const getAlbums = () => {
    return getAllAlbums(setNextPage, getFacebookToken())
      .then(loadAlbums)
      .catch(() => {
        jjLogger.logDebug("useFacebook - getAlbums() : error getting user albums");
      });
  };

  const getImages = (albumId) => {
    return getImagesOfAlbum(albumId, setNextPage, getFacebookToken())
      .then(loadPhotos)
      .catch(() => {
        jjLogger.logDebug("useFacebook - getImages() : error getting photos of album");
      });
  };

  const loadNextPage = (onLoaded) => {
    if (!isNil(nextPage)) {
      return getNextPage(nextPage, setNextPage)
        .then(onLoaded)
        .catch(() => {
          jjLogger.logDebug("useFacebook - loadNextPage() : error getting next page of albums or image");
        });
    }
    return null;
  };

  return {
    albums,
    mode,
    photos,
    initialized,
    changeMode,
    changeModalOpenStorage,
    modalOpen,
    availableAlbums,
    selectedPhoto,
    loading,
    changeSelectedPhoto,
    hasSelectedPhoto,
    status,
    loginFacebook,
    loadNextPage,
    loadAdditionalPhotos,
    loadAdditionalAlbums,
    getAlbums,
    getImages,
    checkLoggedinStatus,
    resetMode,
    clearPhotos,
    clearSelectedPhoto,
    clearAlbums,
    clearStatus,
    storeFacebookToken,
  };
};

export default useFacebook;
